import { formatPercent, NBSP } from "@/v2-common/utils/utils.formatting"
import {
  UPTIME_CATEGORY_DESCRIPTIONS,
  UPTIME_CATEGORY_LABELS,
  UPTIME_CATEGORY_ORDER,
  UPTIME_PLATFORM_LABELS,
  UPTIME_PLATFORM_NEWSECMAPS,
  UPTIME_PLATFORM_ORDER,
  UPTIME_PLATFORM_PLACEPOINT_V1,
  UPTIME_PLATFORM_PLACEPOINT_V2,
  UPTIME_PLATFORM_PROPCLOUD,
  UPTIME_SOURCE_EXTERNAL_NEWSECMAPS,
  UPTIME_SOURCE_EXTERNAL_PROPCLOUD,
  type UptimePlatform,
} from "@/v2-backoffice-client/uptime/uptime.constants"
import {
  getCachedStatusConfig,
  getCachedStatusTables,
} from "@/v2-backoffice-client/uptime/uptime.statusConfigCache"

// The three statuses we ever render. "unknown" is a UI-only fourth
// option used when a target has been configured but never probed.
export type UptimeStatus = "up" | "down" | "skipped" | "unknown"

export function getStatusVariant(
  status: string | null | undefined,
): "success-1" | "destructive" | "warning" | "muted" {
  switch (status) {
    case "up":
      return "success-1"
    case "down":
      return "destructive"
    case "skipped":
      return "warning"
    default:
      return "muted"
  }
}

export function getStatusLabel(status: string | null | undefined): string {
  switch (status) {
    case "up":
      return "Operational"
    case "down":
      return "Down"
    case "skipped":
      return "Skipped"
    default:
      return "Unknown"
  }
}

// Category helpers: turn a raw Source like "app.release" into a
// human-readable title / description, and sort categories so the
// most-interesting ones come first on the board.
//
// Reads first from the cached `getStatusConfig` lookup (operator-
// edited values), then falls back to the literal constants below
// so the UI never goes blank during the first render before the
// config query has resolved.
export function getCategoryLabel(source: string): string {
  const tables = getCachedStatusTables()
  const fromConfig = tables?.categoryBySource.get(source)
  if (fromConfig) return fromConfig.Name
  return UPTIME_CATEGORY_LABELS[source] ?? source
}

/**
 * The href that opens a target's uptime sheet. Targets are keyed
 * (Source, Identifier), and the `v=` param spells that pair with a pipe —
 * a shape worth writing down once, since every surface that links to a
 * target has to produce it byte-for-byte.
 *
 * `push` uses `>>?` so the sheet stacks on whatever is already open
 * (arriving from a layer, say) instead of replacing it.
 */
export function getUptimeSheetHref(
  source: string,
  identifier: string,
  options?: { push?: boolean },
): string {
  const prefix = options?.push ? ">>?" : "?"
  return `${prefix}v=uptime:${source}|${encodeURIComponent(identifier)}`
}

// Parse a hostname out of a target's URL. Returns an empty string for
// anything that isn't a valid absolute URL — the column accessor
// renders a placeholder for empty values, so the operator never sees
// "undefined" or a half-parsed string. Matches the protocol-stripping
// behaviour of `new URL().host` (port included, userinfo dropped).
export function getHostFromUrl(url: string | null | undefined): string {
  if (!url) return ""
  try {
    return new URL(url).host
  } catch {
    return ""
  }
}

// Provider directory: maps a hostname (or hostname suffix) onto the
// human-readable name of the company that runs the service. Order
// matters — entries are checked top-to-bottom and the first match
// wins, so list more-specific suffixes before less-specific ones.
//
// We match on suffix so a multi-region service such as
// "services2.geodataonline.no" still resolves to "Geodata" without
// every variant having to be listed. Hosts not in this directory
// fall back to the bare hostname so the operator at least sees
// *something* identifiable.
const PROVIDER_BY_HOST_SUFFIX: ReadonlyArray<readonly [string, string]> = [
  ["geodataonline.no", "Geodata"],
  ["geodata.no", "Geodata"],
  ["miljodirektoratet.no", "Miljødirektoratet"],
  ["nve.no", "NVE"],
  ["kartverket.no", "Kartverket"],
  ["statkart.no", "Kartverket"],
  ["geonorge.no", "Geonorge"],
  ["vegvesen.no", "Statens vegvesen"],
  ["jernbanedirektoratet.no", "Jernbanedirektoratet"],
  ["banenor.no", "Bane NOR"],
  ["riksantikvaren.no", "Riksantikvaren"],
  ["dsb.no", "DSB"],
  ["nibio.no", "NIBIO"],
  ["norgeibilder.no", "Norge i bilder"],
  ["ssb.no", "SSB"],
  ["oslo.kommune.no", "Oslo Kommune"],
  ["bergen.kommune.no", "Bergen Kommune"],
  ["trondheim.kommune.no", "Trondheim Kommune"],
  ["stavanger.kommune.no", "Stavanger Kommune"],
  ["tromso.kommune.no", "Tromsø Kommune"],
  ["kommune.no", "Kommune"],
  ["placepoint.no", "Placepoint"],
  ["placepoint.io", "Placepoint"],
  // Partner platforms we monitor via plain HTTP probes; mapped to
  // their own provider name so the operator-side "Source" column
  // reads "Propcloud" instead of falling back to a bare hostname.
  ["propcloud.no", "Propcloud"],
  ["newsecmaps.com", "Newsecmaps"],
  // Cloud providers backing each platform. Most-specific suffix
  // first so e.g. `status.firebase.google.com` resolves to "Google
  // Cloud Platform" before the catch-all `googleapis.com` rule.
  ["microsoftonline.com", "Microsoft Azure"],
  ["azureedge.net", "Microsoft Azure"],
  ["azurewebsites.net", "Microsoft Azure"],
  ["azure.com", "Microsoft Azure"],
  ["graph.microsoft.com", "Microsoft Azure"],
  ["aws.amazon.com", "Amazon Web Services"],
  ["signin.aws.amazon.com", "Amazon Web Services"],
  ["amazonaws.com", "Amazon Web Services"],
  ["cloudfront.net", "Amazon Web Services"],
  ["firebase.google.com", "Google Cloud Platform"],
  ["status.firebase.google.com", "Google Cloud Platform"],
  ["cloud.google.com", "Google Cloud Platform"],
  ["status.cloud.google.com", "Google Cloud Platform"],
  ["accounts.google.com", "Google Cloud Platform"],
  ["googleapis.com", "Google Cloud Platform"],
  ["appspot.com", "Google Cloud Platform"],
  ["gstatic.com", "Google Cloud Platform"],
  // localhost / .local hits placepoint dev environments.
  ["localhost", "Placepoint"],
  [".local", "Placepoint"],
]

// Internal app categories ("app.release", "app.staging", …) are all
// served by Placepoint regardless of host (the host is the deploy
// slot's *.azurewebsites.net which would read as nothing useful).
function isPlacepointAppSource(source: string | null | undefined): boolean {
  if (!source) return false
  return source.startsWith("app.")
}

// Source-string -> provider-name shortcut for Source values whose
// host doesn't matter (Placepoint app slots, partner platforms).
// Lookup is one map read so the per-row column accessor stays cheap.
// Note: only the partner's own *product* sources collapse to the
// partner name. Their `.deps` sub-bucket falls through to the host
// directory so e.g. `kart.nve.no` -> "NVE" rather than "Newsecmaps".
const PROVIDER_BY_SOURCE: Record<string, string> = {
  [UPTIME_SOURCE_EXTERNAL_PROPCLOUD]: "Propcloud",
  [UPTIME_SOURCE_EXTERNAL_NEWSECMAPS]: "Newsecmaps",
}

// Provider name for a single uptime target. Falls back to the bare
// host when the directory has no match, and to "—" when there is no
// URL at all so the column never emits a misleading "Placepoint"
// for an unparseable row.
//
// Resolution order: cached `status.Providers` directory (operator-
// edited) -> in-memory PROVIDER_BY_SOURCE / PROVIDER_BY_HOST_SUFFIX
// fallback -> bare host. Operators editing the host-suffix table
// will see the change reflected on the next `getStatusConfig`
// refetch (mutations in the Configuration sheet refetch eagerly).
export function getProviderName(target: {
  Source: string
  Url: string | null | undefined
}): string {
  if (isPlacepointAppSource(target.Source)) return "Placepoint"
  const sourceMatch = PROVIDER_BY_SOURCE[target.Source]
  if (sourceMatch) return sourceMatch
  const host = getHostFromUrl(target.Url).toLowerCase()
  if (!host) return "\u2014"

  const tables = getCachedStatusTables()
  if (tables) {
    for (const { suffix, providerName } of tables.hostSuffixToProvider) {
      if (host === suffix || host.endsWith(`.${suffix}`)) return providerName
    }
  }

  for (const [suffix, name] of PROVIDER_BY_HOST_SUFFIX) {
    if (host === suffix || host.endsWith(`.${suffix}`)) return name
  }
  return host
}

// Platform classification for a single uptime target. Mirrors
// `PlatformFor` in `libs/backends/v2-uptime-data/src/platform.go` so
// the backoffice's grouping agrees with what `v2-status-api` ships
// to the public page.
//
// Defaults to placepoint-v2 because:
//   - All GIS sources (basemap / navlayer / maplayer) are part of
//     the v2 platform; v1 had no map layers in this monitor.
//   - An unknown future Source from a Placepoint app deploy is far
//     more likely to be v2 than v1; v1 is frozen.
//
// Adding a new platform is one new branch here plus a constant in
// `uptime.constants.ts`.
export function getPlatform(target: {
  Source: string
  Identifier: string
}): UptimePlatform {
  // Partner platforms own anything `external.<platform>` plus its
  // `.<sub>` sub-buckets ("api", "deps", "cloud"). HasPrefix keeps
  // the resolver flat so adding a new sub-bucket is purely a
  // manifest change.
  if (
    target.Source === UPTIME_SOURCE_EXTERNAL_PROPCLOUD ||
    target.Source.startsWith(`${UPTIME_SOURCE_EXTERNAL_PROPCLOUD}.`)
  ) {
    return UPTIME_PLATFORM_PROPCLOUD
  }
  if (
    target.Source === UPTIME_SOURCE_EXTERNAL_NEWSECMAPS ||
    target.Source.startsWith(`${UPTIME_SOURCE_EXTERNAL_NEWSECMAPS}.`)
  ) {
    return UPTIME_PLATFORM_NEWSECMAPS
  }
  // Placepoint platforms only show up in `external.*` rows for
  // their cloud-provider sub-bucket (`external.placepoint-v*.cloud`).
  // Their own apps live in `app.*`, handled below.
  if (target.Source.startsWith("external.placepoint-v1.")) {
    return UPTIME_PLATFORM_PLACEPOINT_V1
  }
  if (target.Source.startsWith("external.placepoint-v2.")) {
    return UPTIME_PLATFORM_PLACEPOINT_V2
  }
  if (target.Source.startsWith("app.")) {
    return target.Identifier.startsWith("v1-")
      ? UPTIME_PLATFORM_PLACEPOINT_V1
      : UPTIME_PLATFORM_PLACEPOINT_V2
  }
  return UPTIME_PLATFORM_PLACEPOINT_V2
}

export function getPlatformLabel(platform: UptimePlatform): string {
  const tables = getCachedStatusTables()
  const fromConfig = tables?.platformById.get(platform)
  if (fromConfig) return fromConfig.Name
  return UPTIME_PLATFORM_LABELS[platform]
}

// Compare two platform strings by their canonical render order so
// the sidebar / select-filter sort consistently. Unknown values
// drop to the end (alphabetically among themselves) so a new
// platform shipped before the frontend learns about it still
// renders rather than silently disappearing.
//
// Reads first from the cached config's `Priority` integers when
// available so an operator's reorder takes effect without a code
// change; falls back to the literal `UPTIME_PLATFORM_ORDER` when
// the config hasn't loaded.
export function comparePlatforms(a: string, b: string): number {
  const config = getCachedStatusConfig()
  if (config) {
    const aPlatform = config.Platforms.find((p) => p.Id === a)
    const bPlatform = config.Platforms.find((p) => p.Id === b)
    if (aPlatform && bPlatform) {
      if (aPlatform.Priority !== bPlatform.Priority) {
        return aPlatform.Priority - bPlatform.Priority
      }
      return aPlatform.Name.localeCompare(bPlatform.Name)
    }
    if (aPlatform) return -1
    if (bPlatform) return 1
  }
  const order = UPTIME_PLATFORM_ORDER as readonly string[]
  const ai = order.indexOf(a)
  const bi = order.indexOf(b)
  if (ai === -1 && bi === -1) return a.localeCompare(b)
  if (ai === -1) return 1
  if (bi === -1) return -1
  return ai - bi
}

export function getCategoryDescription(source: string): string | null {
  const tables = getCachedStatusTables()
  const fromConfig = tables?.categoryBySource.get(source)
  if (fromConfig?.Description) return fromConfig.Description
  return UPTIME_CATEGORY_DESCRIPTIONS[source] ?? null
}

export function compareCategories(a: string, b: string): number {
  // Same playbook as comparePlatforms: prefer the cached
  // `Priority` integers (operator-controlled) and only fall back
  // to the canonical literal order when the config isn't loaded.
  const tables = getCachedStatusTables()
  if (tables) {
    const aCat = tables.categoryBySource.get(a)
    const bCat = tables.categoryBySource.get(b)
    if (aCat && bCat) {
      if (aCat.Priority !== bCat.Priority) {
        return aCat.Priority - bCat.Priority
      }
      return aCat.Name.localeCompare(bCat.Name)
    }
    if (aCat) return -1
    if (bCat) return 1
  }
  const order = UPTIME_CATEGORY_ORDER as readonly string[]
  const ai = order.indexOf(a)
  const bi = order.indexOf(b)
  // Anything outside the canonical list drops to the end,
  // alphabetically among itself.
  if (ai === -1 && bi === -1) return a.localeCompare(b)
  if (ai === -1) return 1
  if (bi === -1) return -1
  return ai - bi
}

// Uptime-% formatters. BetterStack-style precision: three meaningful
// decimals for anything under 100, "100%" for a literal 100%.
export function formatUptimePercent(
  pct: number | null | undefined,
  fallback = "\u2014",
): string {
  if (pct === null || pct === undefined) return fallback
  if (pct >= 100) return `100${NBSP}%`
  if (pct >= 99.99) return formatPercent(pct, 3)
  if (pct >= 99) return formatPercent(pct, 2)
  return formatPercent(pct, 1)
}

// Relative time ("22 seconds ago", "3 hours ago"). Small custom
// implementation keeps this feature dependency-free; swap for
// date-fns if we adopt it elsewhere in backoffice.
export function formatRelativeTime(
  iso: string | null | undefined,
  now: Date = new Date(),
): string {
  if (!iso) return "\u2014"
  const then = new Date(iso)
  const diffSec = Math.round((now.getTime() - then.getTime()) / 1000)
  if (!Number.isFinite(diffSec)) return "\u2014"
  if (diffSec < 5) return "just now"
  if (diffSec < 60) return `${diffSec} seconds ago`
  const mins = Math.floor(diffSec / 60)
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`
  const years = Math.floor(days / 365)
  return `${years} year${years === 1 ? "" : "s"} ago`
}

// Duration formatter: "6 months 23 days 10 hours", "28 seconds".
// Used by both the list view (last-checked-at secondary text) and
// the detail view ("Currently up for ...") stat card.
export function formatDurationSince(
  iso: string | null | undefined,
  now: Date = new Date(),
): string {
  if (!iso) return "\u2014"
  const then = new Date(iso).getTime()
  if (!Number.isFinite(then)) return "\u2014"
  const diffSec = Math.max(0, Math.round((now.getTime() - then) / 1000))
  return formatDurationSeconds(diffSec)
}

export function formatDurationSeconds(seconds: number): string {
  if (seconds < 1) return "0 seconds"
  if (seconds < 60) return `${seconds} second${seconds === 1 ? "" : "s"}`

  const parts: string[] = []
  const years = Math.floor(seconds / (365 * 86400))
  let rem = seconds - years * 365 * 86400
  const months = Math.floor(rem / (30 * 86400))
  rem -= months * 30 * 86400
  const days = Math.floor(rem / 86400)
  rem -= days * 86400
  const hours = Math.floor(rem / 3600)
  rem -= hours * 3600
  const mins = Math.floor(rem / 60)
  const secs = rem - mins * 60

  if (years > 0) parts.push(`${years} year${years === 1 ? "" : "s"}`)
  if (months > 0) parts.push(`${months} month${months === 1 ? "" : "s"}`)
  if (days > 0) parts.push(`${days} day${days === 1 ? "" : "s"}`)
  if (hours > 0 && parts.length < 2)
    parts.push(`${hours} hour${hours === 1 ? "" : "s"}`)
  if (mins > 0 && parts.length < 2)
    parts.push(`${mins} minute${mins === 1 ? "" : "s"}`)
  if (parts.length === 0) parts.push(`${secs} second${secs === 1 ? "" : "s"}`)

  // Only keep the two largest units -- "6 months 23 days 10 hours"
  // is noisy enough, going deeper just distracts.
  return parts.slice(0, 3).join(" ")
}

// Keys into the 90-day history array, padded forwards from the
// oldest day the target has so the bar always ends on "today".
export function padDailyHistory<T extends { Date: string }>(
  history: T[],
  days: number,
): (T | null)[] {
  const map = new Map<string, T>()
  for (const d of history) map.set(d.Date, d)

  const out: (T | null)[] = []
  const today = new Date()
  today.setUTCHours(0, 0, 0, 0)

  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today)
    d.setUTCDate(d.getUTCDate() - i)
    const key = d.toISOString().slice(0, 10)
    out.push(map.get(key) ?? null)
  }
  return out
}

// Latency formatter: "<1ms" for tiny values, ms-precision under 1s,
// otherwise "1.2s". Keeps the badge compact across orders of
// magnitude.
export function formatLatency(ms: number | null | undefined): string {
  if (ms == null) return "\u2014"
  if (ms < 1) return "<1 ms"
  if (ms < 1000) return `${Math.round(ms)} ms`
  return `${(ms / 1000).toFixed(ms < 10000 ? 2 : 1)} s`
}

// Latency classification. Uses the target's own 90d P95 as the
// baseline whenever possible so a service that is *naturally* slow
// (e.g. an upstream that routinely takes 2s) doesn't get flagged
// yellow just for being slower than some global threshold.
//
//   "unknown" — no data (first render, no successful probes yet)
//   "fast"    — well under p95 baseline
//   "normal"  — around the baseline band
//   "slow"    — between 1.5x-2x baseline, or 500ms absolute threshold
//   "critical"— >= 2x baseline AND >= 500ms absolute
export type LatencyClass = "unknown" | "fast" | "normal" | "slow" | "critical"

export function classifyLatency(
  currentMs: number | null | undefined,
  baselineMs: number | null | undefined,
): LatencyClass {
  if (currentMs == null) return "unknown"
  if (currentMs <= 0) return "unknown"

  // Without a baseline we fall back to absolute buckets so a fresh
  // target at least renders with something reasonable.
  if (baselineMs == null || baselineMs <= 0) {
    if (currentMs < 300) return "fast"
    if (currentMs < 1000) return "normal"
    if (currentMs < 2500) return "slow"
    return "critical"
  }

  const ratio = currentMs / baselineMs
  if (ratio >= 2 && currentMs >= 500) return "critical"
  if (ratio >= 1.5 && currentMs >= 300) return "slow"
  if (currentMs <= 0.8 * baselineMs) return "fast"
  return "normal"
}

// Per-target severity so the board can sort "most broken first"
// within each category. Higher == worse.
export function targetSeverity(t: {
  LatestStatus: string
  IsSlowOutlier: boolean
}): number {
  if (t.LatestStatus === "down") return 3
  if (t.IsSlowOutlier) return 2
  if (t.LatestStatus === "skipped") return 1
  return 0
}

// Logical status key for table cells / filters. Collapses the raw
// `LatestStatus` and the orthogonal `IsSlowOutlier` flag into a single
// vocabulary so a select filter ("Down", "Slow", "Skipped", "Up") can
// match what the operator sees in the row. Importantly, "slow" wins
// over "up" but loses to "down" and "skipped" so the filter pills
// agree with the severity ordering.
export type TargetStatusKey = "down" | "slow" | "skipped" | "up"
export function targetStatusKey(t: {
  LatestStatus: string
  IsSlowOutlier: boolean
}): TargetStatusKey {
  if (t.LatestStatus === "down") return "down"
  if (t.LatestStatus === "skipped") return "skipped"
  if (t.IsSlowOutlier) return "slow"
  return "up"
}

// Display label for a TargetStatusKey -- used by both the badge in
// the row and the select filter dropdown so the strings line up.
export function getTargetStatusLabel(key: TargetStatusKey): string {
  switch (key) {
    case "down": {
      return "Down"
    }
    case "slow": {
      return "Slow"
    }
    case "skipped": {
      return "Skipped"
    }
    case "up": {
      return "Up"
    }
  }
}

// Walk up from an element to find the nearest ancestor whose overflow
// makes it a scroll container. The backoffice Page renders inside a
// `<main className="page">` with `overflow-auto`, so scroll events
// fire on that element -- not on `window`.
export function findScrollContainer(el: Element | null): HTMLElement | Window {
  let node: Element | null = el
  while (node && node !== document.documentElement) {
    if (node instanceof HTMLElement) {
      const style = window.getComputedStyle(node)
      const overflowY = style.overflowY
      if (
        (overflowY === "auto" ||
          overflowY === "scroll" ||
          overflowY === "overlay") &&
        node.scrollHeight > node.clientHeight
      ) {
        return node
      }
    }
    node = node.parentElement
  }
  return window
}

// Smooth-scroll an element (by id) into view within its own scroll
// container, with a small top offset so the target isn't flush with
// the viewport edge.
export function scrollAnchorIntoView(id: string, offsetPx = 12): boolean {
  const el = document.getElementById(id)
  if (!el) return false
  const container = findScrollContainer(el)
  const rect = el.getBoundingClientRect()
  if (container instanceof HTMLElement) {
    const containerRect = container.getBoundingClientRect()
    const top = container.scrollTop + rect.top - containerRect.top - offsetPx
    container.scrollTo({ top, behavior: "smooth" })
  } else {
    container.scrollTo({
      top: rect.top + window.scrollY - offsetPx,
      behavior: "smooth",
    })
  }
  return true
}
